import styled from "styled-components";
import { theme } from "../theme";

export const ConversationListContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    height: 100%;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch; /* iOS smooth scrolling */
  }
`;

export const ConversationListHeader = styled.div`
  padding: ${theme.spacing.md};
  border-bottom: 1px solid ${theme.colors.border};
  background: ${theme.colors.background.primary};
  flex-shrink: 0;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.sm} ${theme.spacing.md};
    position: sticky;
    top: 0;
    z-index: 10;
  }
`;

export const ConversationListTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${theme.colors.text.primary};
  margin: 0 0 ${theme.spacing.sm} 0;
`;

export const ConversationSearch = styled.div`
  position: relative;
  margin-bottom: ${theme.spacing.sm};
`;

export const SearchInput = styled.input`
  width: 100%;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  font-size: 0.875rem;
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.1);
  }
  
  &::placeholder {
    color: ${theme.colors.text.secondary};
  }
`;

export const ConversationFilter = styled.div`
  display: flex;
  gap: ${theme.spacing.xs};
  margin-bottom: ${theme.spacing.sm};
`;

export const FilterButton = styled.button<{ $active?: boolean }>`
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  background: ${props => props.$active ? theme.colors.primary : theme.colors.background.primary};
  color: ${props => props.$active ? theme.colors.text.light : theme.colors.text.primary};
  font-size: 0.75rem;
  cursor: pointer;
  transition: all ${theme.transitions.normal};
  
  &:hover {
    background: ${props => props.$active ? theme.colors.primary : theme.colors.background.secondary};
  }
`;

export const ConversationItems = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${theme.spacing.xs};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    -webkit-overflow-scrolling: touch; /* iOS smooth scrolling */
    padding: ${theme.spacing.xs};
  }
`;

export const ConversationItem = styled.div<{ $isActive?: boolean }>`
  display: flex;
  align-items: center;
  padding: ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.md};
  cursor: pointer;
  transition: all ${theme.transitions.normal};
  background: ${props => props.$isActive ? 'rgba(102, 126, 234, 0.1)' : 'transparent'};
  border: 1px solid ${props => props.$isActive ? theme.colors.primary : 'transparent'};
  
  &:hover {
    background: ${props => props.$isActive ? 'rgba(102, 126, 234, 0.15)' : theme.colors.background.secondary};
  }
  
  margin-bottom: ${theme.spacing.xs};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.md};
    margin-bottom: ${theme.spacing.sm};
    border-radius: ${theme.borderRadius.lg};
    
    /* Touch-friendly sizing */
    min-height: 60px;
    
    &:active {
      background: ${props => props.$isActive ? 'rgba(102, 126, 234, 0.2)' : theme.colors.background.secondary};
      transform: scale(0.98);
    }
  }
`;

export const ConversationAvatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.text.light};
  font-weight: 600;
  font-size: 1.25rem;
  margin-right: ${theme.spacing.sm};
  flex-shrink: 0;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    width: 52px;
    height: 52px;
    font-size: 1.5rem;
    margin-right: ${theme.spacing.md};
  }
`;

export const ConversationContent = styled.div`
  flex: 1;
  min-width: 0;
`;

export const ConversationHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${theme.spacing.xs};
`;

export const ConversationName = styled.h3`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${theme.colors.text.primary};
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const ConversationTime = styled.span`
  font-size: 0.75rem;
  color: ${theme.colors.text.secondary};
  flex-shrink: 0;
`;

export const ConversationPreview = styled.p`
  font-size: 0.75rem;
  color: ${theme.colors.text.secondary};
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const ConversationBadge = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
`;

export const UnreadBadge = styled.div`
  background: ${theme.colors.primary};
  color: ${theme.colors.text.light};
  font-size: 0.625rem;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 10px;
  min-width: 18px;
  text-align: center;
`;

export const OnlineIndicator = styled.div<{ $isOnline: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => props.$isOnline ? theme.colors.success : theme.colors.text.secondary};
  margin-left: ${theme.spacing.xs};
`;

export const ConversationListLoading = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: ${theme.colors.text.secondary};
`;

export const ConversationListError = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: ${theme.colors.error};
  text-align: center;
  padding: ${theme.spacing.md};
`;

export const EmptyConversationState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: ${theme.colors.text.secondary};
  text-align: center;
  padding: ${theme.spacing.md};
`;

export const EmptyConversationIcon = styled.div`
  font-size: 2rem;
  margin-bottom: ${theme.spacing.sm};
  opacity: 0.5;
`;

export const EmptyConversationText = styled.p`
  font-size: 0.875rem;
  margin: 0;
`;

export const UpgradeButton = styled.button`
  margin-top: ${theme.spacing.md};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  background: linear-gradient(135deg, #FFD700 0%, #FFA500 100%);
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.md};
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all ${theme.transitions.normal};
  box-shadow: 0 4px 6px rgba(255, 215, 0, 0.2);

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 8px rgba(255, 215, 0, 0.3);
  }
`;
